<template>
  <div>
    <v-card-title class="body-2 v-card__subtitle">
      <v-icon small class="mr-2">mdi-account-group</v-icon>
      Leistung nach Benutzergruppe
      <v-spacer></v-spacer>
      <v-chip small outlined text>
        <v-icon small left>mdi-cursor-pointer</v-icon>
        Klicken für Details
      </v-chip>
    </v-card-title>
    <v-card-text v-if="!loading && hasData">
      <VueApexCharts
        type="bar"
        :options="chartOptions"
        :series="chartSeries"
        :height="height"
      ></VueApexCharts>
    </v-card-text>
    <v-card-text
      v-else-if="loading"
      class="d-flex justify-center align-center"
      :style="{ height: height + 'px' }"
    >
      <v-progress-circular indeterminate color="primary"></v-progress-circular>
    </v-card-text>
    <v-card-text
      v-else
      class="d-flex justify-center align-center"
      :style="{ height: height + 'px' }"
    >
      <div class="text-center grey--text">
        <v-icon large color="grey">mdi-chart-bar</v-icon>
        <div class="mt-2">Keine Gruppendaten verfügbar</div>
      </div>
    </v-card-text>
  </div>
</template>

<script>
import VueApexCharts from "vue-apexcharts";
import apexChartsDarkMode from "@/mixins/apexChartsDarkMode";

export default {
  name: "GroupPerformanceChart",
  mixins: [apexChartsDarkMode],
  components: {
    VueApexCharts,
  },
  props: {
    performanceData: {
      type: Array,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    height: {
      type: Number,
      default: 280,
    },
    targets: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {};
  },
  computed: {
    groupedData() {
      const groups = {};

      this.performanceData.forEach((member) => {
        const groupTitle = member.group.title;
        const status = member.trafficLightStatus;

        if (status === "green" || status === "yellow" || status === "red") {
          if (!groups[groupTitle]) {
            groups[groupTitle] = {
              green: 0,
              yellow: 0,
              red: 0,
              total: 0,
              sortKey: member.group.sortKey,
              members: {
                green: [],
                yellow: [],
                red: [],
              },
            };
          }

          groups[groupTitle].total++;
          groups[groupTitle].members[status].push(member);

          if (status === "green") groups[groupTitle].green++;
          else if (status === "yellow") groups[groupTitle].yellow++;
          else if (status === "red") groups[groupTitle].red++;
        }
      });

      return groups;
    },
    hasData() {
      return Object.keys(this.groupedData).length > 0;
    },
    sortedGroupTitles() {
      return Object.keys(this.groupedData).sort(
        (a, b) => this.groupedData[a].sortKey - this.groupedData[b].sortKey
      );
    },
    chartSeries() {
      if (!this.hasData) return [];

      return [
        {
          name: "Grün",
          data: this.sortedGroupTitles.map(
            (title) => this.groupedData[title].green
          ),
        },
        {
          name: "Gelb",
          data: this.sortedGroupTitles.map(
            (title) => this.groupedData[title].yellow
          ),
        },
        {
          name: "Rot",
          data: this.sortedGroupTitles.map(
            (title) => this.groupedData[title].red
          ),
        },
      ];
    },
    chartOptions() {
      const vm = this;
      return {
        ...this.apexChartsDarkModeOptions,
        chart: {
          ...this.apexChartsDarkModeOptions.chart,
          type: "bar",
          stacked: true,
          stackType: "100%",
          toolbar: {
            show: true,
          },
          events: {
            dataPointSelection: function (event, chartContext, config) {
              vm.handleBarClick(config);
            },
          },
        },
        colors: ["#4CAF50", "#FF9800", "#F44336"],
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: "70%",
            dataLabels: {
              position: "center",
            },
          },
        },
        xaxis: {
          categories: this.sortedGroupTitles,
          labels: {
            style: {
              fontSize: "12px",
            },
            rotate: -45,
            rotateAlways: this.sortedGroupTitles.length > 5,
          },
        },
        yaxis: {
          title: {
            text: "Prozentsatz (%)",
          },
          labels: {
            formatter: function (val) {
              return Math.round(val) + "%";
            },
          },
        },
        legend: {
          position: "top",
          horizontalAlign: "center",
        },
        dataLabels: {
          enabled: true,
          formatter: function (val) {
            return val > 5 ? Math.round(val) + "%" : "";
          },
          style: {
            fontSize: "12px",
            colors: ["#fff"],
          },
        },
        tooltip: {
          y: {
            formatter: function (val, { dataPointIndex, w }) {
              const series = w.config.series;
              const total = series.reduce(
                (sum, s) => sum + s.data[dataPointIndex],
                0
              );
              const percentage =
                total > 0 ? ((val / total) * 100).toFixed(1) : 0;
              return val + " (" + percentage + "%)";
            },
          },
        },
        states: {
          hover: {
            filter: {
              type: "darken",
              value: 0.15,
            },
          },
          active: {
            filter: {
              type: "darken",
              value: 0.35,
            },
          },
        },
        noData: {
          text: "Keine Gruppendaten verfügbar",
          align: "center",
          verticalAlign: "middle",
          style: {
            fontSize: "14px",
          },
        },
      };
    },
  },
  methods: {
    handleBarClick(config) {
      const { seriesIndex, dataPointIndex } = config;
      const groupTitle = this.sortedGroupTitles[dataPointIndex];
      const statusMap = ["green", "yellow", "red"];
      const status = statusMap[seriesIndex];

      const members = this.groupedData[groupTitle].members[status];
      const totalMembers = this.performanceData.length;
      const percentage =
        totalMembers > 0
          ? Math.round((members.length / totalMembers) * 100)
          : 0;

      // Prepare member list with summary data
      const memberData = members.map((member) => {
        let metCount = 0;
        let totalCount = 0;

        if (member.performanceTargetResults) {
          Object.values(member.performanceTargetResults).forEach((result) => {
            if (
              result.status !== "notApplicable" &&
              result.status !== "fullySuspended"
            ) {
              totalCount++;
              if (result.status === "met") metCount++;
            }
          });
        }

        return {
          member: member,
          user: member.user,
          group: member.group,
          summary:
            totalCount > 0
              ? `${metCount} von ${totalCount} erfüllt`
              : "Keine Daten",
        };
      });

      // Group members by user group and sort
      const groupedMembers = {};
      memberData.forEach((item) => {
        const groupKey = item.group.title;
        if (!groupedMembers[groupKey]) {
          groupedMembers[groupKey] = {
            sortKey: item.group.sortKey,
            members: [],
          };
        }
        groupedMembers[groupKey].members.push(item);
      });

      // Sort groups and create member list with headers
      const membersWithSummary = [];
      Object.keys(groupedMembers)
        .sort((a, b) => groupedMembers[a].sortKey - groupedMembers[b].sortKey)
        .forEach((groupTitle) => {
          // Add group header
          membersWithSummary.push({
            groupHeader: groupTitle,
          });
          // Add sorted members
          groupedMembers[groupTitle].members
            .sort((a, b) =>
              a.user.displayName.localeCompare(b.user.displayName)
            )
            .forEach((item) => {
              membersWithSummary.push({
                user: item.user,
                summary: item.summary,
              });
            });
        });

      // Emit event to parent with details for sidebar
      this.$emit("segment-selected", {
        type: "memberList",
        chartContext: {
          label: "Leistung nach Gruppe",
          icon: "mdi-account-group",
        },
        title: groupTitle,
        subtitle: this.getStatusLabel(status),
        icon: this.getStatusIcon(status),
        iconColor: this.getStatusColor(status),
        count: members.length,
        countLabel: "Mitglieder",
        summary: [
          {
            label: "Anzahl",
            value: members.length,
            color: this.getStatusColor(status),
          },
          {
            label: "Prozentsatz",
            value: `${percentage}%`,
            color: this.getStatusColor(status),
          },
          {
            label: "Gesamt",
            value: totalMembers,
          },
        ],
        members: membersWithSummary,
      });
    },
    getMemberPerformanceSummary(member) {
      const summary = [];
      let metCount = 0;
      let notMetCount = 0;
      let totalCount = 0;

      if (member.performanceTargetResults) {
        Object.values(member.performanceTargetResults).forEach((result) => {
          totalCount++;
          if (result.status === "met") metCount++;
          else if (result.status === "notMet") notMetCount++;
        });
      }

      if (totalCount > 0) {
        summary.push(`${metCount}/${totalCount} erfüllt`);
        if (notMetCount > 0) {
          summary.push(`${notMetCount} nicht erfüllt`);
        }
      }

      return summary;
    },
    getStatusColor(status) {
      const colors = {
        green: "success",
        yellow: "warning",
        red: "error",
      };
      return colors[status] || "grey";
    },
    getStatusLabel(status) {
      const labels = {
        green: "Grün (Erfüllt)",
        yellow: "Gelb (Teilweise erfüllt)",
        red: "Rot (Nicht erfüllt)",
      };
      return labels[status] || status;
    },
    getStatusIcon(status) {
      const icons = {
        green: "mdi-check-circle",
        yellow: "mdi-alert-circle",
        red: "mdi-close-circle",
      };
      return icons[status] || "mdi-help-circle";
    },
  },
};
</script>

<style scoped>
/* No styles needed anymore, removed local sidebar */
</style>
